import Immutable from 'immutable';
import { formatDescription, getRegistrationStatusMessage } from 'app/helpers/events/utils';

const REQUIRED = 'registration_required';
const WAITLIST = 'registration_waitlisted';

function getOpeningHours(locationHours) {
  const openingHours = [];
  if (!locationHours) return null;

  locationHours.map(hours => {
    if (!hours?.get('openTime') || !hours?.get('closeTime')) return null;
    const timeRef = hours?.get('timeRef');
    const day = timeRef && timeRef?.charAt(0).toUpperCase() + timeRef?.substr(1, 1); // 'monday' => 'Mo'
    const duration = `${hours?.get('openTime')}-${hours?.get('closeTime')}`.replace(/T/g, '');
    return openingHours.push(`${day} ${duration}`);
  });
  return openingHours;
}

function getEventsLinkedData({
  currentLibrary,
  event,
  eventUrl,
  eventLocation,
  languages,
  audiences,
  program,
  types,
  featuredImage
}) {
  let location;
  let inventoryLevel;
  const registrationStatus = getRegistrationStatusMessage(event);
  const description = formatDescription(event.getIn(['definition', 'description'], ''));
  const eventAttendanceMode = event?.getIn(['definition', 'isVirtual'])
    ? 'https://schema.org/OnlineEventAttendanceMode'
    : 'https://schema.org/OfflineEventAttendanceMode';

  // eventStatus
  let eventStatus = 'https://schema.org/EventScheduled';
  if (event?.getIn(['definition', 'isCancelled'])) {
    eventStatus = 'https://schema.org/EventCancelled';
  }

  // keywords
  const keywordsList = Immutable.List([program ? program?.get('name') : null, types.map(type => type.get('name'))])
    .flatten()
    .filter(taxonomy => !!taxonomy);

  // audience
  const eventAudience = Immutable.List([audiences.map(audience => audience.get('name'))])
    .flatten()
    .filter(taxonomy => !!taxonomy);

  const language = languages.getIn([0, 'name']);

  // inventoryLevel
  if (registrationStatus?.id === REQUIRED || registrationStatus?.id === WAITLIST) {
    const numSeats = event?.getIn(['definition', 'registrationInfo', 'cap']) - event.get('numberRegistered');
    inventoryLevel = {
      '@type': 'QuantitativeValue',
      value: numSeats > 0 ? numSeats : 0
    };
  }
  const contact = {
    '@type': 'ContactPoint',
    name: event?.getIn(['definition', 'contact', 'name']),
    email: event?.getIn(['definition', 'contact', 'email', 'globalValue']),
    telephone: event.getIn(['definition', 'contact', 'phone', 'globalValue']),
    contactType: 'Event Information'
  };

  // organizer
  const organizer = {
    '@type': 'Organization',
    name: currentLibrary?.get('longName'),
    url: currentLibrary?.get('cmsUrl'),
    contactPoint: contact?.name || contact?.email || contact?.telephone ? contact : null
  };

  // location
  if (event?.getIn(['definition', 'isVirtual'])) {
    location = {
      '@type': 'VirtualLocation',
      url: eventUrl
    };
  } else {
    const type = event?.getIn(['definition', 'nonBranchLocationId']) ? 'LocalBusiness' : 'Library';
    location = {
      '@type': type,
      name: eventLocation?.get('name'),
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${eventLocation?.getIn(['address', 'number'])} ${eventLocation?.getIn(['address', 'street'])}`,
        addressLocality: eventLocation?.getIn(['address', 'city']),
        addressRegion: eventLocation?.getIn(['address', 'state']),
        postalCode: eventLocation?.getIn(['address', 'zip']),
        addressCountry: eventLocation?.getIn(['address', 'country'])
      },
      openingHours: getOpeningHours(eventLocation?.get('sortedHours')?.map(h => h?.getIn(['hours', 0]))),
      parentOrganization: organizer
    };
  }

  const image = {
    '@type': 'ImageObject',
    url: featuredImage?.get('url'),
    caption: featuredImage?.get('caption')
  };

  const offers = {
    '@type': 'Offer',
    url: eventUrl,
    availability: event?.get('isFull') ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
    validFrom: event?.getIn(['registrationWindow', 'windowStart']) ?? null,
    inventoryLevel
  };

  return Immutable.fromJS({
    '@graph': [
      {
        '@context': 'http://schema.org',
        '@type': 'Event',
        '@id': event?.get('id'),
        url: eventUrl,
        name: event?.getIn(['definition', 'title']),
        description,
        startDate: event?.getIn(['definition', 'start']),
        endDate: event?.getIn(['definition', 'end']),
        eventAttendanceMode,
        eventStatus,
        inLanguage: language,
        location,
        image: featuredImage?.get('url') ? image : null,
        audience: eventAudience?.toJS(),
        organizer,
        offers,
        keywords: keywordsList.toJS()
      }
    ]
  });
}

export { getEventsLinkedData };
